import { ArrowRight } from "lucide-react"
import type { MouseEventHandler, PropsWithChildren } from "react"
import { isValidUrl } from "common/lib/isValidUrl"
import { CallToActionStyle } from "common/types/graphql"
import { Heading } from "common/components/Heading"
import clsx from "classnames"
import { Button } from "common/components/Button"
import { useHasBeenInViewport } from "common/hooks/interaction/useHasBeenInViewport"
import { useEffect } from "react"
import { CallToActionTrackingEventType, trackCallToActionEvent } from "modules/promos/lib/tracking"
import { trackCallToActionEngaged, trackCallToActionViewed } from "common/lib/tcEventCollector"

export interface CallToActionProps {
  href: string
  title?: string
  text: string
  style: CallToActionStyle
  analyticsId: string
  /**
   * Navigation function, overridable in tests since jsdom's `window.location`
   * can no longer be mocked (see jsdom/jsdom#3492).
   */
  navigate?: (href: string) => void
}

export const CallToAction = ({
  href,
  title,
  text,
  style,
  analyticsId,
  navigate = (href) => window.location.assign(href),
}: PropsWithChildren<CallToActionProps>) => {
  const sanitizedHref = isValidUrl(href) ? href : undefined

  const { setRef, hasBeenInViewport } = useHasBeenInViewport(0)

  useEffect(() => {
    if (analyticsId && analyticsId.length > 0 && hasBeenInViewport) {
      // Emit GA4 tracking event
      trackCallToActionEvent(CallToActionTrackingEventType.Impression, analyticsId)
      // Emit in-house tracking event
      trackCallToActionViewed(analyticsId)
    }
  }, [hasBeenInViewport, analyticsId])

  const trackClick: MouseEventHandler<HTMLButtonElement> & MouseEventHandler<HTMLAnchorElement> = (
    event,
  ) => {
    if (analyticsId && analyticsId.length > 0) {
      // Emit GA4 tracking event
      trackCallToActionEvent(CallToActionTrackingEventType.Engagement, analyticsId)
      // Emit in-house tracking event
      trackCallToActionEngaged(analyticsId)

      // Prevent normal link handling, and instead send the user over after 5ms, as this gives the tracking
      // events a chance to get enqueued
      event.preventDefault()
      setTimeout(() => {
        navigate(href)
      }, 5)
    }
  }

  if (title) {
    return (
      <div
        className={clsx("my-6 flex flex-col items-center gap-6 px-6 py-8 text-center", {
          "bg-gray-50 text-black": style === CallToActionStyle.Default,
          "bg-red-500 text-white": style === CallToActionStyle.Donate,
          "bg-indigo-600 text-white": style === CallToActionStyle.Newsletter,
        })}
        ref={setRef}
      >
        <Heading level={3} styledAsLevel={4}>
          {title}
        </Heading>
        <Button
          size="large"
          onDark={style !== CallToActionStyle.Default}
          primary
          href={href}
          onClick={trackClick}
        >
          {text}
        </Button>
      </div>
    )
  }
  return (
    <div className="my-4" ref={setRef}>
      <a
        href={sanitizedHref}
        className="inline-flex gap-3 text-sm font-bold underline hover:text-indigo-600"
        onClick={trackClick}
      >
        {text} <ArrowRight className="inline size-5 flex-none" />
      </a>
    </div>
  )
}

export type CallToActionComponent = typeof CallToAction
